import React, { useEffect, useRef, useState } from 'react';
import { useGetQuery } from '../../../Utils/api';
import { HistoriqueLogs } from '../../../../../common/src/entities';
import { CgArrowLongRight } from 'react-icons/cg';
import './Log.scss';
import { BsChevronDown, BsChevronUp } from 'react-icons/bs';
import TooltipSimple from '../../Common/TooltipSimple';
import DiffMatchPatch from 'diff-match-patch';
import AffichageChargement from './AffichageChargement';
import { DateTime } from 'luxon';
import { TitreCarte } from '../../Common/TitreCarte';
import { MyPaper } from './MyPaper';

const tableStyle: React.CSSProperties = {
    width: '100%',
    borderCollapse: 'collapse',
    marginTop: '10px',
    tableLayout: 'auto',
};

const thStyle: React.CSSProperties = {
    backgroundColor: '#f2f2f2',
    textAlign: 'left',
};

const trStyle: React.CSSProperties = {
    overflow: 'hidden',
};

const tdStyle: React.CSSProperties = {
    position: 'relative',
    maxHeight: '100px',
    overflow: 'hidden',
    borderBottom: '1px solid #ddd',
    paddingRight: '50px',
};

const dateStyle: React.CSSProperties = {
    color: '#32CD32',
};

const userStyle: React.CSSProperties = {
    color: '#6495ED',
};

const boldBlackStyle: React.CSSProperties = {
    fontWeight: 'bold',
    color: 'black',
};

const normalTextStyle: React.CSSProperties = {
    color: 'black',
};

const highlightAddedStyle: React.CSSProperties = {
    backgroundColor: '#d4fcbc', // Light green background
    color: '#2c662d', // Dark green text
};

const highlightRemovedStyle: React.CSSProperties = {
    backgroundColor: '#fddfdf', // Light red background
    color: '#a33a3a', // Dark red text
    textDecoration: 'line-through',
};

export function dateFormatter(date: string): string {
    const dateTime = DateTime.fromISO(date, { zone: 'utc' });
    return dateTime.toFormat('dd-MMM-yy (EEE)').replaceAll('.', '');
}

const isOverflowing = (element: HTMLElement): boolean => {
    return element.scrollHeight > element.clientHeight || element.scrollWidth > element.clientWidth;
};

const getDiffs = (oldText: string, newText: string) => {
    const dmp = new DiffMatchPatch();
    const diffs = dmp.diff_main(oldText, newText);
    dmp.diff_cleanupSemantic(diffs);
    return diffs;
};

const formatLogDetail = (logDetail: string) => {
    const colonIndex = logDetail.indexOf(':');
    if (colonIndex !== -1) {
        const beforeColon = logDetail.substring(0, colonIndex);
        const afterColon = logDetail.substring(colonIndex + 1);
        const parts = afterColon.split('=>');
        if (parts.length === 2) {
            const oldValue = parts[0].trim();
            const newValue = parts[1].trim();
            const diffsOld = getDiffs(oldValue, newValue);
            const diffsNew = getDiffs(oldValue, newValue);

            return (
                <>
                    <span style={boldBlackStyle}>{beforeColon}: </span>
                    {diffsOld.map((part, index) => {
                        const [operation, text] = part;
                        if (operation === DiffMatchPatch.DIFF_DELETE) {
                            return (
                                <span key={index} style={highlightRemovedStyle}>
                                    {text}
                                </span>
                            );
                        }
                        if (operation === DiffMatchPatch.DIFF_EQUAL) {
                            return (
                                <span key={index} style={normalTextStyle}>
                                    {text}
                                </span>
                            );
                        }
                        return null;
                    })}
                    <span>
                        {' '}
                        <CgArrowLongRight />{' '}
                    </span>
                    {diffsNew.map((part, index) => {
                        const [operation, text] = part;
                        if (operation === DiffMatchPatch.DIFF_INSERT) {
                            return (
                                <span key={index} style={highlightAddedStyle}>
                                    {text}
                                </span>
                            );
                        }
                        if (operation === DiffMatchPatch.DIFF_EQUAL) {
                            return (
                                <span key={index} style={normalTextStyle}>
                                    {text}
                                </span>
                            );
                        }
                        return null;
                    })}
                </>
            );
        }
        if (beforeColon.endsWith(' - Ajouté(s)')) {
            const addedValue = beforeColon.replace(' - Ajouté(s)', '').trim();
            return (
                <>
                    <span style={boldBlackStyle}>{addedValue}: </span>
                    <span style={highlightAddedStyle}> {afterColon.trim()}</span>
                </>
            );
        } else if (beforeColon.endsWith(' - Retiré(s)')) {
            const removedValue = beforeColon.replace(' - Retiré(s)', '').trim();
            return (
                <>
                    <span style={boldBlackStyle}>{removedValue}: </span>
                    <span style={highlightRemovedStyle}>{afterColon.trim()}</span>
                </>
            );
        } else {
            return (
                <>
                    <span style={boldBlackStyle}>{beforeColon}: </span>
                    <span style={normalTextStyle}>{afterColon.trim()}</span>
                </>
            );
        }
    }
    return <span style={normalTextStyle}>{logDetail}</span>;
};

const LogsLoader = (props) => {
    const [expandedIndex, setExpandedIndex] = useState<number | null>(null);
    const [overflowStatus, setOverflowStatus] = useState<{ [key: number]: boolean }>({});
    let listLogs;

    if (!props.tableName) {
        listLogs = useGetQuery<HistoriqueLogs[]>('historique_logs');
    } else if (props.id === -1) {
        listLogs = useGetQuery<HistoriqueLogs[]>(`historique_logs/${props.tableName}`);
    } else {
        listLogs = useGetQuery<HistoriqueLogs[]>(`historique_logs/${props.id}/${props.tableName}`);
    }

    const handleChevronClick = (index: number, event: React.MouseEvent) => {
        event.stopPropagation();
        setExpandedIndex(expandedIndex === index ? null : index);
    };

    const textRefs = useRef<(HTMLDivElement | null)[]>(listLogs.data ? new Array(listLogs.data.length) : []);
    useEffect(() => {
        const updateOverflowStatus = () => {
            const statuses: { [key: number]: boolean } = {};
            textRefs.current.forEach((ref, index) => {
                if (ref) {
                    statuses[index] = isOverflowing(ref);
                }
            });
            setOverflowStatus(statuses);
        };

        updateOverflowStatus();
    }, [listLogs.data]);

    return (
        <AffichageChargement query={listLogs}>
            {() => (
                <div className="history-log">
                    {props.noTitle ? '' : <TitreCarte>Historique des logs</TitreCarte>}
                    <MyPaper>
                        {listLogs.data && listLogs.data.length > 0 ? (
                            <table style={tableStyle}>
                                <thead>
                                    <tr>
                                        <th style={thStyle}>Date et Heure</th>
                                        <th style={thStyle}>Utilisateur</th>
                                        {props.hasElement ? <th style={thStyle}>Élément</th> : null}
                                        <th style={thStyle}>Détail</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {[...listLogs.data]
                                        .sort(
                                            (a, b) =>
                                                new Date(b.date_modification).getTime() -
                                                new Date(a.date_modification).getTime(),
                                        )
                                        .map((log, index) => (
                                            <tr style={trStyle} key={index}>
                                                <td className="date-time" style={tdStyle}>
                                                    <span style={dateStyle}>
                                                        {dateFormatter(log.date_modification)}
                                                    </span>
                                                </td>
                                                <td className="user-name" style={tdStyle}>
                                                    <TooltipSimple texte={log.nom_usager}>
                                                        <span style={userStyle}>{log.nom_usager}</span>
                                                    </TooltipSimple>
                                                </td>
                                                {props.hasElement ? (
                                                    <td className="user-name" style={tdStyle}>
                                                        <TooltipSimple texte={log.nom_element}>
                                                            <div>
                                                                <div style={tdStyle}>
                                                                    <strong>{log.nom_table.toUpperCase()}</strong>
                                                                </div>
                                                                <div className="user-name">{log.nom_element}</div>
                                                            </div>
                                                        </TooltipSimple>
                                                    </td>
                                                ) : null}
                                                <td style={tdStyle}>
                                                    <div
                                                        ref={(el) => (textRefs.current[index] = el)}
                                                        className={expandedIndex === index ? '' : 'truncated-text'}
                                                    >
                                                        {log.api_body.split(';').map((logEntry, entryIndex) => (
                                                            <div key={entryIndex}>{formatLogDetail(logEntry)}</div>
                                                        ))}
                                                    </div>
                                                    {overflowStatus[index] && (
                                                        <div>
                                                            <div
                                                                className="chevron-container"
                                                                onClick={(e) => handleChevronClick(index, e)}
                                                            >
                                                                {expandedIndex === index ? (
                                                                    <BsChevronUp />
                                                                ) : (
                                                                    <BsChevronDown />
                                                                )}
                                                            </div>
                                                            <div
                                                                className={
                                                                    expandedIndex === index ? '' : 'white-gradiant'
                                                                }
                                                            ></div>
                                                        </div>
                                                    )}
                                                </td>
                                            </tr>
                                        ))}
                                </tbody>
                            </table>
                        ) : (
                            <p>Aucun log disponible.</p>
                        )}
                    </MyPaper>
                </div>
            )}
        </AffichageChargement>
    );
};

export default LogsLoader;
